// Admin Portal — the city registry behind BuildIQ's "Need New Work?" board.
//
// Hyve staff only (owner ruling 2026-09-12). A city here serves EVERY facility
// that follows it, which is why facilities cannot edit it: one wrong column
// would empty the board for all of them. Offices choose which cities to follow
// inside BuildIQ; this screen decides which cities exist.
//
// The flow is the three questions somebody adding a city has, in order:
//   1. Read columns  — what does this feed have?       POST .../registry/inspect
//   2. Map them      — which column means what?        (form)
//   3. Test pull     — does the row actually READ?     POST .../registry/preview
// then Save (PUT .../registry/{source_id}). There is no delete: switch a city
// off, because its listings and every facility's marks must keep resolving.
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { BUILDIQ_API_URL } from "../../constants";
import {
  MAP_LABELS,
  MULTI_KEYS,
  SINGLE_KEYS,
  applyGuesses,
  emptyForm,
  formFromSource,
  guessFromColumns,
  sourceFromForm,
  suggestSourceId,
} from "./permitSourceForm";

const inputCls =
  "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";
const btn =
  "px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed";

const fmtWhen = (iso) => {
  if (!iso) return "never";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

function PullState({ s }) {
  // Never pulled, pulled and empty, and errored are three different answers.
  if (s.last_error)
    return (
      <span className="text-red-600" title={s.last_error}>
        Failed {fmtWhen(s.last_pull_at)} — {s.last_error.slice(0, 80)}
      </span>
    );
  if (!s.last_pull_at) return <span className="text-slate-400">Never pulled</span>;
  return (
    <span className={s.last_pull_count ? "text-slate-700" : "text-amber-600"}>
      {s.last_pull_count ?? 0} on {fmtWhen(s.last_pull_at)}
    </span>
  );
}

function ColumnSelect({ value, columns, onChange, placeholder = "— none —" }) {
  const names = columns.map((c) => c.name);
  const options = value && !names.includes(value) ? [value, ...names] : names;
  return (
    <select
      className={inputCls}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map((n) => {
        const col = columns.find((c) => c.name === n);
        return (
          <option key={n} value={n}>
            {n}
            {col && col.type ? ` (${col.type})` : ""}
            {!col ? " (saved; not in the columns read)" : ""}
          </option>
        );
      })}
    </select>
  );
}

function ColumnList({ value, columns, onChange }) {
  return (
    <div>
      <div className="flex flex-wrap gap-1 mb-1">
        {value.map((c, i) => (
          <span
            key={`${c}-${i}`}
            className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 rounded px-2 py-0.5 text-xs"
          >
            {c}
            <button
              type="button"
              className="text-slate-400 hover:text-red-600"
              aria-label={`Remove ${c}`}
              onClick={() => onChange(value.filter((_, j) => j !== i))}
            >
              ×
            </button>
          </span>
        ))}
        {!value.length && (
          <span className="text-xs text-slate-400">No columns chosen</span>
        )}
      </div>
      <ColumnSelect
        value=""
        columns={columns.filter((c) => !value.includes(c.name))}
        placeholder="+ add a column"
        onChange={(v) => v && onChange([...value, v])}
      />
    </div>
  );
}

function PreviewResult({ preview }) {
  if (!preview) return null;
  const dropped = Object.entries(preview.dropped || {}).filter(([, n]) => n);
  return (
    <div className="mt-4 border border-slate-200 rounded-lg p-4 bg-white">
      <div className="text-sm text-slate-700">
        Read <strong>{preview.fetched}</strong> rows →{" "}
        <strong>{preview.listings}</strong> listings
        {dropped.length > 0 && (
          <>
            {" "}
            · dropped{" "}
            {dropped.map(([why, n]) => `${n} ${why.replace(/_/g, " ")}`).join(", ")}
          </>
        )}
      </div>
      {preview.warnings && preview.warnings.length > 0 && (
        <ul className="mt-2 space-y-1">
          {preview.warnings.map((w) => (
            <li
              key={w}
              className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
            >
              {w}
            </li>
          ))}
        </ul>
      )}
      {preview.sample && preview.sample.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="py-1 pr-3">Permit</th>
                <th className="py-1 pr-3">Title</th>
                <th className="py-1 pr-3">Address</th>
                <th className="py-1 pr-3">Valuation</th>
                <th className="py-1 pr-3">Issued</th>
                <th className="py-1 pr-3">Status</th>
                <th className="py-1 pr-3">Trade read</th>
                <th className="py-1 pr-3">Position</th>
              </tr>
            </thead>
            <tbody>
              {preview.sample.map((r) => (
                <tr key={r.source_uid} className="border-t border-slate-100 align-top">
                  <td className="py-1 pr-3 whitespace-nowrap">{r.source_uid}</td>
                  <td className="py-1 pr-3 max-w-xs">{r.title}</td>
                  <td className="py-1 pr-3">{r.address || "—"}</td>
                  <td className="py-1 pr-3">
                    {r.valuation != null ? `$${Math.round(r.valuation).toLocaleString()}` : "—"}
                  </td>
                  <td className="py-1 pr-3 whitespace-nowrap">{r.posted_on || "—"}</td>
                  <td className="py-1 pr-3">{r.status || "—"}</td>
                  <td className="py-1 pr-3">{r.section || "—"}</td>
                  <td className="py-1 pr-3">{r.lat != null ? "yes" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Editor({ initial, api, onSaved, onCancel }) {
  const [form, setForm] = useState(initial);
  const [inspected, setInspected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [problems, setProblems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [rawMode, setRawMode] = useState(false);
  const [rawConfig, setRawConfig] = useState("");

  const columns = useMemo(() => (inspected && inspected.columns) || [], [inspected]);
  const set = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setPreview(null); // a preview of a different row is not evidence about this one
  };
  const setMap = (k, v) => set({ map: { ...form.map, [k]: v } });

  const body = () => {
    const src = sourceFromForm(form);
    if (rawMode) {
      try {
        src.config = JSON.parse(rawConfig);
      } catch (e) {
        throw new Error(`The raw config is not valid JSON: ${e.message}`);
      }
    }
    return src;
  };

  const run = async (name, fn) => {
    setBusy(name);
    setMsg(null);
    setProblems([]);
    try {
      await fn();
    } catch (e) {
      setMsg({ type: "err", text: e.message });
      if (e.problems) setProblems(e.problems);
    } finally {
      setBusy(null);
    }
  };

  const readColumns = () =>
    run("inspect", async () => {
      const d = await api("/api/fn/prospects/registry/inspect", {
        method: "POST",
        body: JSON.stringify({ driver: form.driver, base_url: form.base_url }),
      });
      setInspected(d);
      const patch = { base_url: d.base_url };
      if (form.isNew && d.max_page) patch.page = Math.min(d.max_page, 1000);
      if (form.isNew && form.driver === "esri") patch.geometry = !!d.has_geometry;
      setForm((f) =>
        applyGuesses({ ...f, ...patch }, guessFromColumns(d.columns, form.driver)),
      );
      setPreview(null);
    });

  const testPull = () =>
    run("preview", async () => {
      setPreview(await api("/api/fn/prospects/registry/preview", {
        method: "POST",
        body: JSON.stringify(body()),
      }));
    });

  const save = () =>
    run("save", async () => {
      const src = body();
      const d = await api(
        `/api/fn/prospects/registry/${encodeURIComponent(src.source_id)}`,
        { method: "PUT", body: JSON.stringify(src) },
      );
      onSaved(d.source, d.created);
    });

  const toggleRaw = () => {
    if (!rawMode) setRawConfig(JSON.stringify(sourceFromForm(form).config, null, 2));
    setRawMode(!rawMode);
    setPreview(null);
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">
          {form.isNew ? "Add a city" : `Edit ${form.label || form.source_id}`}
        </h3>
        <button type="button" className={`${btn} text-slate-600`} onClick={onCancel}>
          Close
        </button>
      </div>

      {/* 1 — the feed */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>City (stamped on every listing)</label>
          <input
            className={inputCls}
            value={form.area}
            onChange={(e) => {
              const area = e.target.value;
              const patch = { area };
              if (form.isNew) {
                patch.source_id = suggestSourceId(area);
                if (!form.label || form.label.endsWith(" building permits"))
                  patch.label = area ? `${area} building permits` : "";
              }
              set(patch);
            }}
          />
        </div>
        <div>
          <label className={labelCls}>State (two letters — never assumed)</label>
          <input
            className={inputCls}
            maxLength={2}
            value={form.state}
            onChange={(e) => set({ state: e.target.value.toUpperCase() })}
          />
        </div>
        <div>
          <label className={labelCls}>Source id</label>
          <input
            className={inputCls}
            value={form.source_id}
            disabled={!form.isNew}
            title={form.isNew ? "" : "Every listing and mark is keyed to this; it cannot change."}
            onChange={(e) => set({ source_id: e.target.value })}
          />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Label</label>
          <input
            className={inputCls}
            value={form.label}
            onChange={(e) => set({ label: e.target.value })}
          />
        </div>
        <div>
          <label className={labelCls}>Protocol</label>
          <select
            className={inputCls}
            value={form.driver}
            onChange={(e) => {
              set({ driver: e.target.value });
              setInspected(null);
            }}
          >
            <option value="esri">ArcGIS REST (MapServer / FeatureServer layer)</option>
            <option value="socrata">Socrata (resource .json)</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>
            Feed address — {form.driver === "esri" ? "the layer URL, e.g. …/FeatureServer/0" : "…/resource/xxxx-xxxx.json"}
          </label>
          <input
            className={inputCls}
            value={form.base_url}
            onChange={(e) => set({ base_url: e.target.value })}
            placeholder="https://"
          />
        </div>
        <div className="flex items-end">
          <button
            type="button"
            className={`${btn} bg-blue-600 text-white w-full`}
            disabled={!form.base_url || busy}
            onClick={readColumns}
          >
            {busy === "inspect" ? "Reading…" : "Read columns"}
          </button>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Public page for this data (optional)</label>
          <input
            className={inputCls}
            value={form.portal_url}
            onChange={(e) => set({ portal_url: e.target.value })}
            placeholder="https://"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Days back</label>
            <input
              type="number"
              min={1}
              max={3650}
              className={inputCls}
              value={form.default_days}
              onChange={(e) => set({ default_days: e.target.value })}
            />
          </div>
          <div>
            <label className={labelCls}>Rows per page</label>
            <input
              type="number"
              min={1}
              max={5000}
              className={inputCls}
              value={form.page}
              onChange={(e) => set({ page: e.target.value })}
            />
          </div>
        </div>
      </section>

      {inspected && (
        <div className="text-xs text-slate-500">
          {inspected.columns.length} columns read
          {inspected.max_page ? ` · the service sends at most ${inspected.max_page} rows per page` : ""}
          {form.driver === "esri" && !inspected.has_geometry &&
            " · this layer is a table with no geometry — positions can only come from latitude/longitude columns"}
          {form.driver === "socrata" &&
            " · Socrata omits empty fields, so a column blank on every sample row is not listed"}
        </div>
      )}

      {/* 2 — what each column means */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-slate-700">Which column means what</h4>
          <button type="button" className="text-xs text-blue-600" onClick={toggleRaw}>
            {rawMode ? "Back to the form" : "Edit the raw config instead"}
          </button>
        </div>
        {rawMode ? (
          <textarea
            className={`${inputCls} font-mono text-xs h-72`}
            value={rawConfig}
            onChange={(e) => {
              setRawConfig(e.target.value);
              setPreview(null);
            }}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Permit number (required)</label>
              <ColumnSelect
                value={form.id_field}
                columns={columns}
                onChange={(v) => {
                  const col = columns.find((c) => c.name === v);
                  set({
                    id_field: v,
                    id_is_number: col
                      ? ["integer", "smallinteger", "biginteger", "double", "oid"].includes(col.type)
                      : form.id_is_number,
                  });
                }}
              />
            </div>
            <div>
              <label className={labelCls}>Issue date the window counts back from (required)</label>
              <ColumnSelect
                value={form.date_field}
                columns={columns}
                onChange={(v) =>
                  set({
                    date_field: v,
                    map: { ...form.map, posted_on: form.map.posted_on || v },
                  })
                }
              />
            </div>
            {MULTI_KEYS.map((k) => (
              <div key={k}>
                <label className={labelCls}>
                  {MAP_LABELS[k]}
                  {k === "title" ? " (required)" : ""}
                </label>
                <ColumnList
                  value={form.map[k]}
                  columns={columns}
                  onChange={(v) => setMap(k, v)}
                />
              </div>
            ))}
            {SINGLE_KEYS.map((k) => (
              <div key={k}>
                <label className={labelCls}>{MAP_LABELS[k]}</label>
                <ColumnSelect
                  value={form.map[k]}
                  columns={columns}
                  onChange={(v) => setMap(k, v)}
                />
              </div>
            ))}
            {form.driver === "esri" && (
              <div className="md:col-span-2 flex flex-wrap gap-6 text-sm text-slate-700">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.geometry}
                    disabled={inspected ? !inspected.has_geometry : false}
                    onChange={(e) => set({ geometry: e.target.checked })}
                  />
                  Take the position from the layer's geometry
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.id_is_number}
                    onChange={(e) => set({ id_is_number: e.target.checked })}
                  />
                  Permit number is stored as a number
                </label>
              </div>
            )}
          </div>
        )}
      </section>

      {/* 3 — does it read */}
      <section className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className={`${btn} bg-slate-700 text-white`}
          disabled={!!busy}
          onClick={testPull}
        >
          {busy === "preview" ? "Pulling one page…" : "Test pull"}
        </button>
        <button
          type="button"
          className={`${btn} bg-emerald-600 text-white`}
          disabled={!!busy || !preview}
          title={preview ? "" : "Run a test pull first — a row that has never read is not ready to save."}
          onClick={save}
        >
          {busy === "save" ? "Saving…" : form.isNew ? "Save city" : "Save changes"}
        </button>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => set({ active: e.target.checked })}
          />
          Active — pulled nightly and offered to facilities
        </label>
      </section>

      {msg && (
        <div className={`text-sm ${msg.type === "err" ? "text-red-600" : "text-emerald-700"}`}>
          {msg.text}
        </div>
      )}
      {problems.length > 0 && (
        <ul className="list-disc ml-5 text-sm text-red-600">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      <PreviewResult preview={preview} />
    </div>
  );
}

export default function PermitSources() {
  const { token } = useAuth();
  const [sources, setSources] = useState(null);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState(null);

  const api = async (path, options = {}) => {
    const res = await fetch(`${BUILDIQ_API_URL}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.detail || data.error || `HTTP ${res.status}`);
      err.problems = data.problems || [];
      throw err;
    }
    return data;
  };

  const load = () =>
    api("/api/fn/prospects/registry")
      .then((d) => setSources(d.sources || []))
      .catch((e) => setMsg({ type: "err", text: e.message }));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSaved = (src, created) => {
    setEditing(null);
    setMsg({
      type: "ok",
      text: `${src.label} ${created ? "added" : "saved"}. It is pulled on the next nightly run.`,
    });
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Permit sources</h2>
          <p className="text-sm text-slate-500 max-w-3xl">
            The cities BuildIQ's “Need New Work?” board reads. Each city serves every
            facility that follows it. Facilities choose which cities to follow inside
            BuildIQ; this list decides which cities exist.
          </p>
        </div>
        {!editing && (
          <button
            type="button"
            className={`${btn} bg-blue-600 text-white whitespace-nowrap`}
            onClick={() => setEditing(emptyForm())}
          >
            Add a city
          </button>
        )}
      </div>

      {msg && (
        <div className={`text-sm ${msg.type === "err" ? "text-red-600" : "text-emerald-700"}`}>
          {msg.text}
        </div>
      )}

      {editing && (
        <Editor
          key={editing.source_id || "new"}
          initial={editing}
          api={api}
          onSaved={onSaved}
          onCancel={() => setEditing(null)}
        />
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        {sources === null ? (
          <div className="p-6 text-sm text-slate-500">Loading…</div>
        ) : sources.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No cities are set up yet.</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500">
              <tr>
                <th className="px-4 py-2">City</th>
                <th className="px-4 py-2">Protocol</th>
                <th className="px-4 py-2">Last pull</th>
                <th className="px-4 py-2">Listings</th>
                <th className="px-4 py-2">Facilities following</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.source_id} className="border-t border-slate-100">
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-800">
                      {s.area}, {s.state}
                      {!s.active && (
                        <span className="ml-2 text-xs text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
                          off
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{s.label}</div>
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {s.driver === "esri" ? "ArcGIS" : "Socrata"}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <PullState s={s} />
                  </td>
                  <td className="px-4 py-2">{s.listings}</td>
                  <td className="px-4 py-2">{s.followers}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      className="text-blue-600 text-sm"
                      onClick={() => setEditing(formFromSource(s))}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
